import React, { FC, useEffect, useState } from "react";
import { Grid, Segment, Header } from "semantic-ui-react";
import "./SummaryView.scss";
import { DashGridItem } from "../DashGrid";
import { useCentralCalls } from "@src/rest/CentralCalls";

interface Props {
    projectVersionId: string;
    item: DashGridItem;
    userId: string;
    data:any;
}

interface Category {
    name: string;
    color: string;
    startDate: Date;
    endDate: Date;
    subcategories: { name: string }[];
}

const categoryColors: string[] = [
    "#6F3166",
    "#E15759",
    "#76B7B2",
    "#EDC949",
    "#4E79A7"
];

const calculateYearLabels = (startDate: Date, endDate: Date) => {
    const years: string[] = [];
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();

    for (let year = endYear; year >= startYear; year--) {
        years.unshift(year === endYear ? "Launch Year" : `-${endYear - year} Years`);
    }

    return years;
};

const calculateCategoryPosition = (categoryStart: Date, categoryEnd: Date, startDate: Date, endDate: Date, totalYears: number) => {
    const totalTimelineDuration = endDate.getTime() - startDate.getTime();
    const categoryStartOffset = categoryStart.getTime() - startDate.getTime();
    const categoryEndOffset = categoryEnd.getTime() - startDate.getTime();

    const startPercent = (categoryStartOffset / totalTimelineDuration) * 100;
    const endPercent = (categoryEndOffset / totalTimelineDuration) * 100;

    return {
        start: Math.round((startPercent / 100) * totalYears) + 1,
        end: Math.round((endPercent / 100) * totalYears) + 1
    };
};

const SummaryView: FC<Props> = ({ projectVersionId, data, item }) => {
    //const { executeStoredProcedure } = useCentralCalls();
    const [ categories, setCategories ] = useState<Category[]>( [] ),
          [ startDate, setStartDate ]   = useState<Date | null>( null ),
          [ endDate, setEndDate ]       = useState<Date | null>( null );

    useEffect(() => {
        const fetchData = async () => {
            try {
             
                //const data = await executeStoredProcedure("proc_GetExecutiveSummary", { projectVersionId });
                if (Array.isArray(data) && data.length > 0) {
                    const formattedCategories: Category[] = data.map((item: any, index: number) => ({
                        name: item.name,
                        color: categoryColors[index % categoryColors.length], // Assign a color
                        startDate: new Date(item.startDate),
                        endDate: new Date(item.endDate),
                        subcategories: item.subcategories || [] // Ensure it's an array of objects
                    }));
                    const sortedCategories = formattedCategories.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

                    setCategories(sortedCategories);

                    // Compute the lowest startDate and highest endDate
                    const minStartDate = new Date(
                        Math.min(...formattedCategories.map((c) => c.startDate.getTime()))
                    );
                    const maxEndDate = new Date(
                        Math.max(...formattedCategories.map((c) => c.endDate.getTime()))
                    );

                    setStartDate(minStartDate);
                    setEndDate(maxEndDate);
                }
            } catch (error) {
                console.error("Error fetching categories:", error);
            }
        };

        fetchData();
    }, [projectVersionId]);

    if (!startDate || !endDate) return <p></p>;

    const years = calculateYearLabels(startDate, endDate);
    const totalYears = years.length;

    return (
        <div className="summary-view">
            <Grid>
                <Grid.Row>
                    <Grid.Column>
                        <h2>Every Step Is Critical for Long-Term Success</h2>
                    </Grid.Column>
                </Grid.Row>

                <Grid.Row columns={years.length} className="year-header">
                    {years.map((label, index) => (
                        <Grid.Column key={index}>
                            <Segment style={{ backgroundColor: "#235E76", color: "white" }}>{label}</Segment>
                        </Grid.Column>
                    ))}
                </Grid.Row>
            </Grid>

            <div className="category-grid" style={{ gridTemplateColumns: `repeat(${totalYears}, 1fr)` }}>
                {categories.map((category, index) => {
                    const { start, end } = calculateCategoryPosition(
                        category.startDate,
                        category.endDate,
                        startDate,
                        endDate,
                        totalYears
                    );

                    return (
                        <div
                            key={index}
                            className="category-segment"
                            style={{
                                gridColumnStart: start,
                                gridColumnEnd: end,
                                backgroundColor: category.color
                            }}
                        >
                            <Header as="h4">{category.name}</Header>
                            <ul>
                                <li>{category.subcategories.map((sub: { name: string }) => sub.name).join(", ")}</li>
                            </ul>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default SummaryView;